import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { api } from '../api';
import { STATIC } from '../staticMode';
import { glossStore } from '../staticStore';

const LOCAL_KEY = 'seediq_gloss';

// Backend-backed custom meanings (per user). Loads the whole map into state so
// getGloss stays synchronous; writes persist to the server and mirror to
// localStorage as an offline backup. Existing browser glosses migrate up once.
export function useGloss() {
  const { token } = useAuth();
  const [glosses, setGlosses] = useState(() => (STATIC ? glossStore.getAll() : {}));

  useEffect(() => {
    if (STATIC) return;                          // static: personal glosses from localStorage (above)
    if (!token) { setGlosses({}); return; }
    let alive = true;
    api('/glosses', { token }).then(server => {
      const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
      // Merge: this device's edits win, so a save that failed to reach the
      // server is never wiped — and we re-push so the server catches up.
      const merged = { ...server, ...local };
      if (Object.keys(local).length) {
        api('/glosses/bulk', { method: 'POST', token, body: merged }).catch(() => {});
      }
      if (alive) {
        setGlosses(merged);
        localStorage.setItem(LOCAL_KEY, JSON.stringify(merged));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [token]);

  const getGloss = useCallback((word) => {
    if (!word) return null;
    return glosses[word.toLowerCase()] ?? null;
  }, [glosses]);

  const setGloss = useCallback((word, gloss) => {
    if (!word) return;
    const key = word.toLowerCase();
    setGlosses(prev => {
      const next = { ...prev };
      if (gloss === null || gloss === '') delete next[key];
      else next[key] = gloss;
      if (!STATIC) localStorage.setItem(LOCAL_KEY, JSON.stringify(next));  // dev: offline backup
      return next;
    });
    if (STATIC) { glossStore.set(key, gloss); return; }
    if (token) api('/glosses', { method: 'PUT', token, body: { word: key, gloss: gloss || '' } }).catch(() => {});
  }, [token]);

  return { getGloss, setGloss };
}
